import { useEffect, useRef } from 'react';
import { trackScrollDepth, trackTimeOnPage, trackPageVisibility } from '../utils/analytics';

// Hook for tracking scroll depth
export const useScrollDepth = () => {
  const scrollDepthsTracked = useRef(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = Math.round(((scrollTop + windowHeight) / documentHeight) * 100);

      // Track at 25%, 50%, 75%, 90%, 100%
      const milestones = [25, 50, 75, 90, 100];
      milestones.forEach(milestone => {
        if (scrollPercentage >= milestone && !scrollDepthsTracked.current.has(milestone)) {
          scrollDepthsTracked.current.add(milestone);
          trackScrollDepth(milestone);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

// Hook for tracking time on page
export const useTimeOnPage = () => {
  const startTime = useRef(Date.now());
  const lastTracked = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const timeSpent = Math.floor((Date.now() - startTime.current) / 1000);
      
      // Track every 30 seconds
      if (timeSpent > 0 && timeSpent % 30 === 0 && timeSpent !== lastTracked.current) {
        lastTracked.current = timeSpent;
        trackTimeOnPage(timeSpent);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);
};

// Hook for tracking page visibility
export const usePageVisibility = () => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      trackPageVisibility(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
};

// Combined hook for all page analytics
export const usePageAnalytics = () => {
  useScrollDepth();
  useTimeOnPage();
  usePageVisibility();
};

